import jwtAuthKioskAxios from "../auth/jwtAuthKiosk";
import { setBackendDomain } from "./management";
import { systemSettingsRequestConfig } from "./dsokSystemSettingsApi";

/**
 * Billing (faktúrácia) API — backend-node /dsok/billing/*.
 * Nastavenia faktúrácie (dsokSettings.billing) sa ukladajú cez bežný customer settings save;
 * tieto endpointy sú pre faktúry (manuálna + zoznam/detail).
 */

/**
 * GET /dsok/billing/:customerId/abra-partner — načíta firemné údaje odberateľa z Abry
 * podľa kódu (query `code`, inak uložený abraPartnerCode). Nič neukladá; vráti dáta na naplnenie.
 * @param {number|string} customerId
 * @param {string} [code] - aktuálny kód z formulára (nemusí byť uložený)
 */
export const syncBillingFromAbra = async (customerId, code) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.get(
    `/dsok/billing/${customerId}/abra-partner`,
    { params: code ? { code } : {} },
  );
  return data;
};

/** GET /dsok/billing/:customerId/invoices — zoznam faktúr zákazníka. */
export const getBillingInvoices = async (customerId) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.get(
    `/dsok/billing/${customerId}/invoices`,
  );
  return data;
};

/**
 * GET /dsok/billing/invoices — globálny zoznam všetkých vygenerovaných faktúr + DPH sumár.
 * @param {{ dateFrom?:string, dateTo?:string, status?:string, customerId?:number|string }} [filters]
 * @returns {Promise<{ success:boolean, data:object[], summary:{ count:number, withoutVat:number, vat:number, withVat:number } }>}
 */
export const getAllBillingInvoices = async (filters = {}) => {
  setBackendDomain(jwtAuthKioskAxios);
  const params = {};
  if (filters.dateFrom) params.dateFrom = filters.dateFrom;
  if (filters.dateTo) params.dateTo = filters.dateTo;
  if (filters.status) params.status = filters.status;
  if (filters.customerId != null && filters.customerId !== "")
    params.customerId = filters.customerId;
  // Server-side stránkovanie (1-based page). Bez nich backend vráti prvú stránku (default 20).
  if (filters.page != null) params.page = filters.page;
  if (filters.pageSize != null) params.pageSize = filters.pageSize;
  const { data } = await jwtAuthKioskAxios.get(`/dsok/billing/invoices`, {
    params,
  });
  return data;
};

/**
 * GET /dsok/billing/invoices/:invoiceId/delivery-status — reálny stav doručenia
 * emailu zákazníkovi (z notif servera / Brevo).
 */
export const getBillingInvoiceDeliveryStatus = async (invoiceId) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.get(
    `/dsok/billing/invoices/${invoiceId}/delivery-status`,
  );
  return data;
};

/** GET /dsok/billing/invoices/:invoiceId — detail faktúry. */
export const getBillingInvoice = async (invoiceId) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.get(
    `/dsok/billing/invoices/${invoiceId}`,
  );
  return data;
};

/**
 * POST /dsok/billing/:customerId/invoices/manual — manuálna faktúra.
 * @param {number|string} customerId
 * @param {{ year:number, month?:number, frequency?:string, overrides?:object }} payload
 */
export const createManualInvoice = async (customerId, payload) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post(
    `/dsok/billing/${customerId}/invoices/manual`,
    payload,
  );
  return data;
};

/**
 * GET /dsok/billing/invoices/:invoiceId/pdf — stiahne PDF faktúry (proxy z Abry).
 * @returns {Promise<Blob>}
 */
export const downloadBillingInvoicePdf = async (invoiceId) => {
  setBackendDomain(jwtAuthKioskAxios);
  const res = await jwtAuthKioskAxios.get(
    `/dsok/billing/invoices/${invoiceId}/pdf`,
    { responseType: "blob" },
  );
  return res.data;
};

/**
 * POST /dsok/billing/invoices/:invoiceId/send — odošle faktúru emailom (za flagom).
 * @param {string} invoiceId
 * @param {boolean} force - true = odoslať aj keď už bola raz odoslaná
 */
export const sendBillingInvoiceEmail = async (invoiceId, force = false) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post(
    `/dsok/billing/invoices/${invoiceId}/send`,
    { force },
  );
  return data;
};

/**
 * POST /dsok/billing/invoices/:invoiceId/send-reminder — odošle upomienku (faktúra po splatnosti).
 * @param {string} invoiceId
 * @param {"email"|"sms"} channel - kanál upomienky (default email)
 * @param {boolean} force - true = odoslať aj keď už bola dnes zaslaná
 * @param {{ test?: boolean, overrideEmail?: string, overridePhone?: string, baseUrl?: string }} [opts]
 *   test=true → neposiela reálnemu zákazníkovi, ale na testovací kontakt (override*).
 *   baseUrl → (LEN pre test) explicitný cieľový backend, prebije getBEUrl() pre toto volanie.
 */
export const sendBillingInvoiceReminder = async (
  invoiceId,
  channel = "email",
  force = false,
  opts = {},
) => {
  setBackendDomain(jwtAuthKioskAxios);
  const cfg =
    opts.test && opts.baseUrl
      ? { baseURL: `${String(opts.baseUrl).replace(/\/+$/, "")}/` }
      : undefined;
  const { data } = await jwtAuthKioskAxios.post(
    `/dsok/billing/invoices/${invoiceId}/send-reminder`,
    {
      channel,
      force,
      ...(opts.test ? { test: true } : {}),
      ...(opts.overrideEmail ? { overrideEmail: opts.overrideEmail } : {}),
      ...(opts.overridePhone ? { overridePhone: opts.overridePhone } : {}),
    },
    cfg,
  );
  return data;
};

/** PUT /dsok/billing/invoices/:invoiceId/status — manuálna zmena stavu faktúry. */
export const updateBillingInvoiceStatus = async (invoiceId, status) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.put(
    `/dsok/billing/invoices/${invoiceId}/status`,
    { status },
  );
  return data;
};

/** DELETE /dsok/billing/invoices/:invoiceId — zmaže lokálny záznam faktúry (testovanie). */
export const deleteBillingInvoice = async (invoiceId) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.delete(
    `/dsok/billing/invoices/${invoiceId}`,
  );
  return data;
};

/**
 * POST /dsok/billing/run — manuálne spustenie faktúračného cyklu (všetci zákazníci).
 * @returns {Promise<{ success:boolean, data?:object, reason?:string }>}
 */
export const runBillingCycleNow = async () => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post(`/dsok/billing/run`, {});
  return data;
};

/**
 * POST /dsok/billing/refresh-payments — import stavu úhrady faktúr z Abry.
 * @returns {Promise<{ success:boolean, data?:object, reason?:string }>}
 */
export const refreshBillingPayments = async () => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post(
    `/dsok/billing/refresh-payments`,
    {},
  );
  return data;
};

/** POST /dsok/billing/:customerId/refresh-payments — import stavu úhrady z Abry pre zákazníka. */
export const refreshCustomerBillingPayments = async (customerId) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post(
    `/dsok/billing/${customerId}/refresh-payments`,
    {},
  );
  return data;
};

/**
 * POST /dsok/billing/test-email-dryrun — dry-run testu odosielania faktúr emailom.
 * Pre všetkých zákazníkov so zapnutou faktúráciou overí pripravenosť (cez notif server),
 * NIČ neodošle.
 * @returns {Promise<{ success:boolean, data?:{ summary:object, results:object[] } }>}
 */
export const testBillingEmailDryRun = async () => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post(
    `/dsok/billing/test-email-dryrun`,
    {},
  );
  return data;
};

/**
 * POST /dsok/billing/cron-runs/:runId/send-emails — reálne hromadné odoslanie faktúr
 * daného behu emailom. Dá sa iba RAZ (server stráži cez emailsSentAt).
 * @param {string} runId
 */
export const sendBillingRunEmails = async (runId) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post(
    `/dsok/billing/cron-runs/${runId}/send-emails`,
    {},
  );
  return data;
};

/**
 * POST /dsok/billing/cron-runs/:runId/send-selected-emails — odošle IBA vybrané faktúry
 * behu emailom. Výber sa odovzdáva ako identita výsledkov behu (customerId + invoiceNumber
 * + periodKey); per-faktúra guard bráni duplicite a už odoslané sa preskočia.
 * @param {string} runId
 * @param {Array<{customerId:number|string, invoiceNumber?:string, periodKey?:string}>} selections
 */
export const sendSelectedBillingRunEmails = async (runId, selections) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post(
    `/dsok/billing/cron-runs/${runId}/send-selected-emails`,
    { selections: Array.isArray(selections) ? selections : [] },
  );
  return data;
};

/**
 * POST /dsok/billing/cron-runs/:runId/resend-failed — doposlanie faktúr behu,
 * ktorých email cez Brevo zlyhal/nedoručený. Doručené a prebiehajúce sa preskočia.
 * @param {string} runId
 */
export const resendFailedBillingRunEmails = async (runId) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post(
    `/dsok/billing/cron-runs/${runId}/resend-failed`,
    {},
  );
  return data;
};

/** GET /dsok/billing/cron-runs — globálny zoznam behov faktúrácie. */
export const getBillingCronRuns = async (limit = 50) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.get(
    `/dsok/billing/cron-runs?limit=${limit}`,
  );
  return data;
};

/** GET /dsok/billing/cron-runs/:runId — detail jedného behu. */
export const getBillingCronRun = async (runId) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.get(
    `/dsok/billing/cron-runs/${runId}`,
  );
  return data;
};

/** GET /dsok/billing/:customerId/cron-runs — behy dotýkajúce sa zákazníka. */
export const getCustomerBillingCronRuns = async (customerId, limit = 20) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.get(
    `/dsok/billing/${customerId}/cron-runs?limit=${limit}`,
  );
  return data;
};

/** GET /dsok/billing/system-settings — normalizované systémové nastavenia faktúrácie. */
export const getBillingSystemSettings = async () => {
  // Systémové (globálne) nastavenia — cieľový backend podľa hostiteľa AdminUI
  // (rovnako ako ich ukladanie), NIE cez per-customer beDomain.
  const { data } = await jwtAuthKioskAxios.get(
    `/dsok/billing/system-settings`,
    systemSettingsRequestConfig(),
  );
  return data;
};

/** GET /dsok/billing/cron-status — read-only stav automatického cronu. */
export const getBillingCronStatus = async () => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.get(`/dsok/billing/cron-status`);
  return data;
};
